import React from 'react';
import styled from 'styled-components';
import { COLOR, FONT_SIZE, SPACING } from '../../../constants/theme';

const Row = styled.div`
  display: ${({ stacked }) => (stacked ? 'block' : 'flex')};
  /* The large emphasised amount drops below its label when they don't fit. */
  flex-wrap: ${({ emphasised }) => (emphasised ? 'wrap' : 'nowrap')};
  align-items: ${({ emphasised }) => (emphasised ? 'baseline' : 'flex-start')};
  justify-content: space-between;
  gap: ${({ emphasised }) =>
    emphasised ? `${SPACING.xs} ${SPACING.lg}` : SPACING.lg};
  padding: ${({ emphasised }) => (emphasised ? '14px 0 0' : '11px 0')};
  border-bottom: ${({ emphasised }) =>
    emphasised ? 'none' : `1px solid ${COLOR.border}`};
  border-top: ${({ emphasised }) =>
    emphasised ? `2px solid ${COLOR.navy}` : 'none'};
  margin-top: ${({ emphasised }) => (emphasised ? SPACING.xs : '0')};

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  font-size: ${({ emphasised }) =>
    emphasised ? FONT_SIZE.value : FONT_SIZE.label};
  font-weight: ${({ emphasised }) => (emphasised ? 700 : 400)};
  color: ${({ emphasised }) => (emphasised ? COLOR.navy : COLOR.textSecondary)};
`;

const SubLabel = styled.span`
  display: block;
  margin-top: 2px;
  font-size: ${FONT_SIZE.hint};
  font-weight: 400;
  color: ${COLOR.textSecondary};
`;

const Value = styled.span`
  display: ${({ stacked }) => (stacked ? 'block' : 'inline')};
  margin-top: ${({ stacked }) => (stacked ? SPACING.xs : '0')};
  margin-left: ${({ emphasised, stacked }) =>
    emphasised && !stacked ? 'auto' : '0'};
  font-size: ${({ emphasised }) => (emphasised ? '23px' : FONT_SIZE.value)};
  font-weight: ${({ emphasised, stacked }) => {
    if (emphasised) return 700;
    return stacked ? 400 : 500;
  }};
  color: ${({ emphasised }) => (emphasised ? COLOR.navy : COLOR.text)};
  text-align: ${({ stacked }) => (stacked ? 'left' : 'right')};
  line-height: ${({ emphasised, stacked }) => {
    if (!stacked) return 'inherit';
    return emphasised ? 1.3 : 1.55;
  }};
  white-space: ${({ stacked, $nowrap }) => {
    if ($nowrap) return 'nowrap';
    return stacked ? 'pre-wrap' : 'normal';
  }};
  font-variant-numeric: tabular-nums;

  /* A gap fee range at 23px is wider than the card on the smallest phones. */
  @media (max-width: 360px) {
    font-size: ${({ emphasised }) => (emphasised ? '20px' : FONT_SIZE.value)};
  }
`;

const Currency = styled.span`
  margin-left: 3px;
  font-size: ${FONT_SIZE.hint};
  font-weight: 500;
  color: ${COLOR.textSecondary};
`;

const DetailRow = ({
  label,
  subLabel,
  value,
  currency,
  emphasised,
  badge,
  stacked,
  nowrap,
}) => (
  <Row emphasised={emphasised} stacked={stacked}>
    <Label emphasised={emphasised}>
      {label}
      {badge}
      {subLabel && <SubLabel>{subLabel}</SubLabel>}
    </Label>
    {/* An amount, such as "$149.00 – $199.00", never splits across lines. */}
    <Value
      emphasised={emphasised}
      stacked={stacked}
      $nowrap={nowrap || Boolean(currency)}
    >
      {value}
      {currency && <Currency>{currency}</Currency>}
    </Value>
  </Row>
);

export default DetailRow;
